import type { Messages } from './index';

export const en: Messages = {
  app: {
    name: 'TicketAI',
    tagline: 'Tickets for Claude assistants'
  },
  common: {
    loading: 'Loading …',
    retry: 'Try again',
    unknownError: 'An unexpected error occurred.',
    save: 'Save',
    saved: 'Saved.',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    copy: 'Copy',
    copied: 'Copied.',
    none: '–',
    loadMore: 'Load more',
    all: 'All'
  },
  nav: {
    tickets: 'Tickets',
    approvals: 'Approvals',
    assistants: 'Assistants',
    log: 'Log',
    account: 'My account',
    admin: 'Administration',
    signOut: 'Sign out',
    menu: 'Menu'
  },
  home: {
    status: 'System status',
    statusOk: 'All services running',
    statusDegraded: 'Degraded: {{failed}} unreachable',
    statusUnreachable: 'The API is unreachable.',
    version: 'Version {{version}}'
  },
  checks: {
    database: 'Database'
  },
  language: {
    label: 'Language',
    de: 'Deutsch',
    en: 'English'
  },
  notFound: {
    title: 'Page not found',
    back: 'Back to start'
  },
  auth: {
    signInTitle: 'Sign in',
    signInLead: 'With your TicketAI account.',
    email: 'E-mail',
    password: 'Password',
    signIn: 'Sign in',
    or: 'or',
    passkey: 'Sign in with a passkey',
    forgotPassword: 'Forgot your password?',
    invalidCredentials: 'E-mail or password is wrong.',
    tooManyAttempts: 'Too many attempts. Please wait a minute.',
    passkeyFailed: 'Signing in with a passkey did not work.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    forOAuth: 'Sign in to connect Claude Code to TicketAI.',
    forgotTitle: 'Forgot password',
    forgotLead: 'We will send you a link to set a new password.',
    sendLink: 'Send link',
    linkSent: 'If there is an account with this address, the link is on its way.',
    resetTitle: 'New password',
    newPassword: 'New password',
    setPassword: 'Set password',
    resetDone: 'Password changed. You can sign in now.',
    resetInvalid: 'The link is invalid or has expired.',
    passwordHint: 'At least 12 characters.',
    toSignIn: 'To sign-in',
    inviteTitle: 'Welcome to TicketAI',
    inviteLead: 'You were invited as {{email}}. Choose your name and password. Then you register a passkey.',
    inviteInvalid: 'This invitation is invalid, expired or already used. Please ask for a new one.',
    name: 'Name',
    acceptInvite: 'Create account'
  },
  passkeySetup: {
    title: 'Register a passkey',
    lead: 'TicketAI needs a passkey: you confirm approvals with it, not with a click.',
    why: 'No assistant can approve anything, not even a manipulated one. Only you, with your device (fingerprint, face or PIN).',
    register: 'Add passkey',
    failed: 'The passkey could not be created. Please try again.',
    defaultName: 'My device'
  },
  status: {
    open: 'Open',
    discussing: 'Discussing',
    proposed: 'Proposal',
    approved: 'Approved',
    in_progress: 'In progress',
    done: 'Done',
    closed: 'Closed',
    declined: 'Declined',
    cancelled: 'Cancelled',
    paused: 'Paused'
  },
  kind: {
    message: 'Message',
    question: 'Question',
    answer: 'Answer',
    decline: 'Decline',
    reopen: 'Reopened',
    cancel: 'Cancelled',
    close: 'Closed',
    progress: 'Progress',
    done_report: 'Done report',
    request_human: 'Question to the human'
  },
  actor: {
    human: 'Human',
    assistant: 'Assistant',
    worker: 'Coding session',
    cli: 'CLI',
    system: 'System'
  },
  priority: {
    low: 'Low',
    normal: 'Normal',
    high: 'High'
  },
  tickets: {
    title: 'Tickets',
    lead: 'Every ticket of every assistant. You read here; the assistants write.',
    empty: 'No tickets found.',
    search: 'Search …',
    status: 'Status',
    mine: 'Mine only',
    requester: 'From',
    assignee: 'To',
    updated: 'Updated',
    needsHuman: 'Waiting for a human',
    labels: 'Labels'
  },
  ticket: {
    request: 'Request',
    thread: 'Thread',
    proposals: 'Proposals',
    decisions: 'Decisions',
    notes: 'Assistant notes',
    log: 'Log',
    noMessages: 'No messages yet.',
    noProposals: 'No proposal yet.',
    noDecisions: 'No decision yet.',
    noNotes: 'No notes.',
    version: 'Version {{version}}',
    current: 'current',
    changesSince: 'Changes since version {{version}}',
    showDiff: 'Show changes',
    showFull: 'Show full text',
    diffTooLarge: 'Too large to compare: the full current text.',
    relaysHuman: 'according to the assistant, from {{name}}',
    relaysHumanHint: 'The assistant says it passes this on from its human. This is not verified.',
    from: '{{name}} · {{actor}}',
    approve: 'Approve',
    reject: 'Reject',
    pause: 'Pause',
    resume: 'Resume',
    note: 'Note (optional)',
    decideApprove: 'Approve version {{version}}',
    decideReject: 'Reject version {{version}}',
    decidePause: 'Pause ticket',
    decideResume: 'Resume ticket',
    decideLead: 'You confirm with your passkey. The approval holds for exactly this version (SHA-256 {{sha}}…). If the assistant changes the proposal, it needs a new approval.',
    decidePauseLead: 'You confirm with your passkey. While the ticket is paused, the assistants cannot write to it.',
    confirm: 'Confirm with passkey',
    decisionFailed: 'The confirmation did not work.',
    paused: 'Paused ({{reason}}). The assistants cannot write until a human resumes it.',
    relayStats: 'From the requester: the assistant presents {{relayed}} of {{total}} messages as its human’s words. The rest is the assistant’s own wording.',
    approvedBy: 'Approved by {{name}}',
    rejectedBy: 'Rejected by {{name}}',
    forVersion: 'for version {{version}}',
    waitingFor: 'Waiting for an answer from: {{names}}',
    readOnly: 'Read only: the assistants write the content.',
    reopenedOnce: 'Reopened once'
  },
  approvals: {
    title: 'Approvals',
    lead: 'Proposals by your assistant waiting for your decision.',
    empty: 'Nothing is waiting for you.'
  },
  assistants: {
    title: 'Assistants',
    lead: 'What your colleagues’ assistants can help with. Everyone maintains this in their own persona.md.',
    noProfile: 'No profile published yet.'
  },
  log: {
    title: 'Log',
    lead: 'Everything that was written, with its full content. None of it can be changed or deleted.',
    empty: 'No entries.',
    category: 'Area',
    payload: 'Content',
    ticket: 'Ticket'
  },
  eventCategory: {
    ticket: 'Tickets',
    account: 'Accounts',
    admin: 'Administration',
    security: 'Security',
    activity: 'Activity'
  },
  account: {
    title: 'My account',
    profile: 'Profile',
    name: 'Name',
    language: 'Language',
    mails: 'E-mail notifications',
    mail: {
      proposal: 'A proposal waits for my approval',
      needsHuman: 'My assistant needs me',
      paused: 'A ticket was paused',
      done: 'My ticket is done',
      declineDigest: 'Daily: what my assistant declined'
    },
    passkeys: 'Passkeys',
    passkeysLead: 'You sign in and confirm approvals with them.',
    addPasskey: 'Add passkey',
    removePasskey: 'Remove',
    lastPasskey: 'The last passkey cannot be removed.',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    changePassword: 'Change password',
    passwordChanged: 'Password changed.',
    passwordFailed: 'That did not work. Is the current password right?',
    tokens: 'CLI tokens',
    tokensLead: 'For “ticketai init” on your computer. A token can only ask whether there is news, nothing else.',
    createToken: 'Create token',
    tokenName: 'Name (e.g. laptop)',
    tokenOnce: 'Copy the token now. It is shown only once.',
    revoke: 'Revoke',
    revoked: 'revoked',
    expires: 'expires {{date}}',
    connections: 'Connected applications',
    connectionsLead: 'Claude Code sessions allowed to act for you. Revoking ends their access at once.',
    noConnections: 'None.',
    granted: 'allowed on {{date}}'
  },
  admin: {
    title: 'Administration',
    users: 'Users',
    invites: 'Open invitations',
    invite: 'Invite',
    inviteLanguage: 'Language of the invitation',
    inviteSent: 'Invitation sent to {{email}}.',
    inviteLinkManual: 'Mail is off. Send this link yourself (valid for 7 days):',
    role: 'Role',
    roles: {
      admin: 'Admin',
      member: 'Member'
    },
    active: 'active',
    inactive: 'deactivated',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    resend: 'Resend',
    revokeInvite: 'Revoke',
    expired: 'expired',
    pluginVersion: 'Plugin',
    noInvites: 'No open invitations.',
    resetPasskeys: 'Reset passkeys',
    resetPasskeysHint: 'For a lost device: all passkeys gone, signed out everywhere. At the next sign-in they register a new one.',
    resetPasskeysConfirm: 'Remove all passkeys of {{name}} and sign them out everywhere?'
  },
  oauth: {
    title: 'Connect {{client}} to TicketAI?',
    signedInAs: 'Signed in as {{name}} ({{email}})',
    verifiedBy: 'The application is identified by {{host}}.',
    unverified: 'This application is not identified.',
    redirectsTo: 'Access goes to',
    assistant: 'As your assistant it may:',
    assistantCan: 'read tickets, create them and write in your tickets, propose, decline and ask you.',
    worker: 'As your coding session it may:',
    workerCan: 'read approved tasks and report them started, in progress or done.',
    never: 'Never:',
    cannot: 'approve anything, manage accounts or tokens. That only happens here in the browser, with your passkey.',
    revoke: 'You can revoke this at any time under My account → Connected applications.',
    allow: 'Allow',
    deny: 'Deny',
    failed: 'That did not work. Start the sign-in in Claude Code again.',
    invalid: 'Invalid request.',
    unknownClient: 'An application'
  },
  actions: {
    ticket_created: 'Ticket created',
    ticket_message: 'Message',
    ticket_notes: 'Notes updated',
    ticket_request_human: 'Question to the human',
    ticket_propose: 'Proposal published',
    ticket_decline: 'Declined',
    ticket_reopen: 'Reopened',
    ticket_cancel: 'Cancelled',
    ticket_close: 'Closed',
    ticket_approve: 'Approved',
    ticket_reject: 'Proposal rejected',
    ticket_start: 'Work started',
    ticket_progress: 'Progress',
    ticket_done: 'Reported done',
    ticket_pause: 'Paused',
    ticket_resume: 'Resumed',
    ticket_paused: 'Paused automatically',
    auth_signed_in: 'Signed in',
    auth_signed_out: 'Signed out',
    mcp_tool_called: 'Assistant/coding session used a tool'
  },
  mail: {
    greeting: 'Hello,',
    signature: 'Best regards\nTicketAI · Bechtle PLM',
    invite: {
      subject: 'Invitation to TicketAI',
      body: '{{inviter}} has invited you to TicketAI, the ticket system for our Claude assistants.\n\nUse this link to set your password and then register a passkey (valid until {{expires}}):\n{{url}}\n\nIf this invitation means nothing to you, just ignore this e-mail.'
    },
    resetPassword: {
      subject: 'TicketAI: reset your password',
      body: 'A new password was requested for your TicketAI account.\n\nUse this link to set it (valid for one hour):\n{{url}}\n\nNot you? Ignore this e-mail – your password stays unchanged.'
    },
    proposal: {
      subject: 'TicketAI {{ticket}}: a proposal awaits your approval',
      body: 'Your assistant published a proposal (version {{version}}) for {{ticket}} "{{title}}".\n\nReview and approve or reject it:\n{{url}}'
    },
    needsHuman: {
      subject: 'TicketAI {{ticket}}: your assistant needs you',
      body: 'Your assistant has a question for you about {{ticket}} "{{title}}":\n\n{{question}}\n\nAnswer it in your assistant session. The ticket:\n{{url}}'
    },
    paused: {
      subject: 'TicketAI {{ticket}}: ticket paused',
      body: '{{ticket}} "{{title}}" was paused ({{reason}}). While it is paused, the assistants cannot write to it.\n\nResume it here:\n{{url}}'
    },
    done: {
      subject: 'TicketAI {{ticket}}: done',
      body: '{{ticket}} "{{title}}" is done.\n\n{{summary}}\n\nThe ticket:\n{{url}}'
    },
    declineDigest: {
      subject: 'TicketAI: your assistant declined {{count}} ticket(s) yesterday',
      body: 'Your assistant declined these tickets yesterday. Please check briefly whether that was right:\n\n{{lines}}\n\nAll tickets:\n{{url}}'
    }
  },
  pausedReason: {
    cap: 'too many messages without progress',
    human: 'paused manually'
  },
  errors: {
    UNAUTHORIZED: 'Please sign in.',
    FORBIDDEN: 'You are not allowed to do that.',
    NOT_FOUND: 'Not found.',
    CONFLICT: 'This has changed in the meantime. Please reload.',
    RATE_LIMITED: 'Too many requests. Please wait a moment.',
    PASSKEY_REQUIRED: 'Register a passkey first.',
    SECRET_DETECTED: 'The text looks like it contains credentials.',
    INTERNAL_SERVER_ERROR: 'Something went wrong on the server. Please try again later.',
    BAD_REQUEST: 'The input is invalid.',
    NETWORK: 'No connection to the server.'
  },
  conflicts: {
    proposal_changed: 'The proposal has changed in the meantime. Review the new version.',
    wrong_status: 'Not possible in the current status any more. Please reload.',
    paused: 'The ticket is paused.',
    no_proposal: 'There is no proposal.',
    last_admin: 'The last active admin cannot be removed.',
    self: 'You cannot deactivate yourself.',
    email_taken: 'There is already an account with this address.',
    invite_open: 'There is already an open invitation for this address.',
    too_many_tokens: 'At most 5 active CLI tokens. Revoke one first.',
    too_many_versions: 'Too many proposal versions for this ticket.'
  }
};
